/**
 * PreorderButton — V144.
 *
 * Renders a "Pre-order €29" CTA next to coming-soon agents that have
 * crossed the demand threshold (default 25 signups). Clicking it
 * creates a Stripe Checkout session and redirects.
 *
 * Visibility is read from the same /demand cache as DemandCounter
 * to avoid extra fetches.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { CreditCard, Loader2 } from "lucide-react";
import { API } from "../../config";
import { useLanguage } from "../../contexts/LanguageContext";
import { funnelT } from "../../translations-funnel";

let _cache = { at: 0, signups: {}, threshold: 25, inflight: null };
const TTL_MS = 5 * 60 * 1000;

async function fetchDemand() {
  const now = Date.now();
  if (_cache.at && now - _cache.at < TTL_MS) return _cache;
  if (_cache.inflight) return _cache.inflight;
  _cache.inflight = (async () => {
    try {
      const r = await axios.get(`${API}/agent-waitlist/demand`);
      const signups = {};
      for (const row of r.data.rows || []) signups[row.agent_slug] = row.signups || 0;
      _cache = {
        at: now,
        signups,
        threshold: r.data.preorder_threshold || 25,
        amount: r.data.preorder_amount_eur || 29,
        inflight: null,
      };
      return _cache;
    } catch {
      _cache.inflight = null;
      return _cache;
    }
  })();
  return _cache.inflight;
}

export default function PreorderButton({ agentSlug, agentName, email }) {
  const { language } = useLanguage();
  const ft = funnelT(language);
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    fetchDemand().then((c) => {
      if (cancelled) return;
      setVisible((c.signups[agentSlug] || 0) >= (c.threshold || 25));
    });
    return () => { cancelled = true; };
  }, [agentSlug]);

  if (!visible) return null;

  const submit = async () => {
    if (!email || !email.includes("@")) {
      setErr(ft.wl_email_invalid);
      return;
    }
    setLoading(true);
    setErr("");
    try {
      const r = await axios.post(`${API}/agent-waitlist/preorder`, {
        agent_slug: agentSlug,
        email,
        success_url: `${window.location.origin}/agent/${agentSlug}?preorder=success`,
        cancel_url: `${window.location.origin}/agent/${agentSlug}?preorder=cancelled`,
      });
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
    } catch (e) {
      setLoading(false);
      setErr(e?.response?.data?.detail || ft.wl_error_generic);
    }
  };

  return (
    <div className="mt-2 border-t border-amber-500/30 pt-3" data-testid={`preorder-block-${agentSlug}`}>
      <p className="text-[10px] uppercase tracking-[0.2em] font-black text-amber-300 mb-2">
        {ft.preorder_eyebrow}
      </p>
      <button
        onClick={submit}
        disabled={loading}
        data-testid={`preorder-btn-${agentSlug}`}
        className="w-full bg-emerald-500 text-black font-black px-3 py-2 text-[11px] uppercase tracking-widest hover:bg-emerald-400 disabled:opacity-50 flex items-center justify-center gap-1.5"
      >
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CreditCard className="w-3.5 h-3.5" />}
        {ft.preorder_label}
      </button>
      <p className="text-[10px] text-gray-500 mt-1.5 leading-relaxed">
        {ft.preorder_subtext}
      </p>
      {err ? <p className="text-[10px] text-red-400 mt-1">{err}</p> : null}
    </div>
  );
}
